import {FeatureBandwidthEstimationMessageId} from '@Features/BandwidthEstimation/Constants/FeatureBandwidthEstimationMessageId';
import {MessageNamespace} from '@Logger/Constants/MessageNamespace';
import {logger} from '@Logger/logger';
import type {IDownloaderEndOfDownloadMetricsEvent} from '@Network/Types/TDownloaderEvent';
import {AssetType} from '@Shared/Constants/AssetType';

import type {Metrics} from '../Models/Metrics';
import {updateEwmaValue} from '../Util/updateEwmaValue';

type TNetworkActions = {
    addThroughputBasedBandwidthSample(bandwidthSample: number): void;
    addEndOfFileMetrics(endOfDownloadMetrics: IDownloaderEndOfDownloadMetricsEvent): void;
    setActiveVariantIndex(activeVariantIndex: number): void;
    reseedEwmaFromThroughputBasedBandwidthEstimate(): void;
    resetThroughputBasedBandwidthSamples(): void;
    resetTimeBasedBandwidthEstimates(): void;
};

/**
 * Resolve Network Actions
 *
 * @remark
 * WARNING: Depo no longer supports array object proxies. Any array in a Depo store must not be manipulated using array
 * functions. The whole array must be replaced in a single operation.
 */
const resolveNetworkActions = ({network}: Metrics): TNetworkActions => ({
    addThroughputBasedBandwidthSample(bandwidthSample) {
        const nextThroughputBasedBandwidthSamples = [...network.throughputBasedBandwidthSamples, bandwidthSample];

        if (nextThroughputBasedBandwidthSamples.length > network.configAbr.maxRollingAverageSamples) {
            nextThroughputBasedBandwidthSamples.shift();
        }

        // See WARNING in resolveNetworkActions comment before changing the following line of code.
        network.throughputBasedBandwidthSamples = nextThroughputBasedBandwidthSamples;

        logger.info(
            MessageNamespace._003_FEATURE_BANDWIDTH_ESTIMATION,
            FeatureBandwidthEstimationMessageId._001_THROUGHPUT_BASED_ESTIMATE_CHANGED,
            network.humanReadableThroughputBasedBandwidthEstimate,
        );
    },

    addEndOfFileMetrics({assetType, contentDownloadDurationMs, requestLatencyMs, throughputMultiplier, totalBytes}) {
        const nextRequestLatencySamples = [...network.requestLatencySamples, requestLatencyMs];

        if (nextRequestLatencySamples.length > network.configAbr.maxRollingAverageSamples) {
            nextRequestLatencySamples.shift();
        }

        // See WARNING in resolveNetworkActions comment before changing the following line of code.
        network.requestLatencySamples = nextRequestLatencySamples;

        logger.info(
            MessageNamespace._003_FEATURE_BANDWIDTH_ESTIMATION,
            FeatureBandwidthEstimationMessageId._002_LATENCY_ESTIMATE_CHANGED,
            `${network.requestLatencyEstimate}ms`,
        );

        if (assetType === AssetType.VIDEO_SEGMENT) {
            const contentDownloadDurationSeconds = contentDownloadDurationMs / 1000;

            const bandwidth = ((totalBytes * 8) / contentDownloadDurationSeconds) * throughputMultiplier;

            network.timeBasedBandwidthEwmaFast = updateEwmaValue(
                network.timeBasedBandwidthEwmaFast,
                bandwidth,
                network.configAbr.ewmaHalfLifeFast,
            );

            network.timeBasedBandwidthEwmaSlow = updateEwmaValue(
                network.timeBasedBandwidthEwmaSlow,
                bandwidth,
                network.configAbr.ewmaHalfLifeSlow,
            );

            logger.info(
                MessageNamespace._003_FEATURE_BANDWIDTH_ESTIMATION,
                FeatureBandwidthEstimationMessageId._003_TIME_BASED_ESTIMATE_CHANGED,
                network.humanReadableTimeBasedBandwidthEstimate,
            );
        }
    },

    setActiveVariantIndex(activeVariantIndex) {
        network.activeVariantIndex = activeVariantIndex;
    },

    /**
     * Reseed the EWMA time based bandwidth estimate with the throughput based bandwidth estimate if it is less than the
     * current time based bandwidth estimate.
     *
     * @remark
     * It is possible that before the buffer duration deteriorates, triggering this function to be called, for the
     * throughput based bandwidth estimate to be stale if downloads complete before the progress event is sent. Checking
     * that the throughput based bandwidth estimate is less than the current time based bandwidth will not prevent the
     * use of a stale throughput based bandwidth estimate from being used, but does ensure that we don't increase the
     * time based bandwidth estimate.
     *
     * If the throughput based estimate is inaccurate the Segment Orchestrator choose variant functionality will
     * compensate, by decreasing or increasing as required. If the buffer deteriorates further, it is likely that this
     * function will be called again with an updated throughput based bandwidth estimate, but if it doesn't then Segment
     * Orchestrator will attempt to compensate.
     */
    reseedEwmaFromThroughputBasedBandwidthEstimate() {
        if (
            isNaN(network.throughputBasedBandwidthEstimate) ||
            network.throughputBasedBandwidthEstimate >= network.timeBasedBandwidthEstimate
        ) {
            logger.log(
                MessageNamespace._003_FEATURE_BANDWIDTH_ESTIMATION,
                FeatureBandwidthEstimationMessageId._005_RESEEDING_PREVENTED,
                network.humanReadableThroughputBasedBandwidthEstimate,
                network.humanReadableTimeBasedBandwidthEstimate,
            );

            return;
        }

        network.timeBasedBandwidthEwmaFast = network.throughputBasedBandwidthEstimate;
        network.timeBasedBandwidthEwmaSlow = network.throughputBasedBandwidthEstimate;
    },

    resetThroughputBasedBandwidthSamples() {
        network.throughputBasedBandwidthSamples = [];
    },

    resetTimeBasedBandwidthEstimates() {
        network.timeBasedBandwidthEwmaFast = -1;
        network.timeBasedBandwidthEwmaSlow = -1;
    },
});

export type {TNetworkActions};
export {resolveNetworkActions};
